/* Contenuti delle pagine /funzionalita/:slug.
   `inArrivo: true` marca ciò che non è ancora nel prodotto: la pagina lo
   mostra con il badge «In arrivo», così la promessa resta visibile ma onesta. */

export interface Capacita {
  title: string;
  copy: string;
  inArrivo?: boolean;
}

export interface Passo {
  title: string;
  copy: string;
}

export interface Numero {
  figure: string;
  unit: string;
  label: string;
}

export interface Funzionalita {
  slug: string;
  code: string;
  name: string;
  /** Una riga per le card che rimandano alla pagina. */
  teaser: string;
  /** Titolo della pagina; `mark` è la parte evidenziata in colore. */
  title: string;
  mark: string;
  lead: string;
  /** Per chi è: ruoli che la usano ogni giorno. */
  perChi: string;
  problema: { title: string; copy: string };
  capacita: Capacita[];
  passi?: Passo[];
  numeri?: Numero[];
  pull: string;
}

export const FUNZIONALITA: Funzionalita[] = [
  {
    slug: 'gestione-commessa',
    code: '01',
    name: 'Gestione della commessa',
    teaser: 'Tutta la commessa in una schermata: dati, persone, documenti, email, compiti e numeri.',
    title: 'Tutta la commessa,',
    mark: 'in una schermata.',
    lead: "BOSS è il project management della commessa edile: anagrafica, organigramma, documenti, email, compiti e cockpit economico nello stesso posto. Chi entra in una commessa trova tutto, chi la dirige sa sempre a che punto è.",
    perChi: 'Project manager · direttori tecnici · geometri · ufficio tecnico',
    problema: {
      title: 'Oggi la commessa vive in dieci posti diversi.',
      copy: "Il contratto in una cartella, le email nella casella di qualcuno, i documenti su un drive, i numeri in un Excel che aggiorna una sola persona. Quando quella persona è in ferie, la commessa si ferma.",
    },
    capacita: [
      {
        title: 'Cockpit di commessa',
        copy: 'Costi, ricavi, margine e avanzamento della commessa in un colpo d’occhio, aggiornati dal lavoro di ogni giorno.',
      },
      {
        title: 'Anagrafica e organigramma',
        copy: 'Committente, importi, date, ruoli di commessa: chi è il PM, chi il capocantiere, chi firma cosa.',
      },
      {
        title: 'Documenti ed email',
        copy: 'Allegati, PDF su carta intestata ed email di Outlook collegati alla commessa, inviabili da BOSS.',
      },
      {
        title: 'Compiti e scadenze',
        copy: 'Chi deve fare cosa, entro quando. I compiti aperti e chiusi restano nella storia della commessa.',
      },
      {
        title: 'Avvio guidato',
        copy: 'Una checklist di avvio per ogni nuova commessa, e la Denuncia Nuovo Lavoro pronta dai dati già inseriti.',
      },
      {
        title: 'Piano di commessa e Gantt',
        copy: 'Le lavorazioni pianificate nel tempo, collegate ai compiti e all’avanzamento reale.',
        inArrivo: true,
      },
    ],
    passi: [
      {
        title: 'La gara vinta diventa commessa',
        copy: 'I dati della gara passano alla commessa: niente da ricopiare.',
      },
      {
        title: 'Il cantiere la alimenta',
        copy: 'Rapportini, DDT, mezzi e ore aggiornano la commessa ogni giorno.',
      },
      {
        title: 'La direzione la legge',
        copy: 'Margine e avanzamento sono già lì, senza chiedere un report.',
      },
    ],
    pull: 'Una commessa, una schermata, una sola versione dei fatti.',
  },
  {
    slug: 'contabilita-analitica',
    code: '02',
    name: 'Contabilità analitica di cantiere',
    teaser: 'Il costo reale di ogni commessa, voce per voce, e il margine mentre il cantiere lavora.',
    title: 'Il margine di ogni cantiere,',
    mark: 'mentre lavora.',
    lead: "BOSS ricostruisce il costo reale di ogni commessa dal dato di campo — manodopera, subappalti, materiali, mezzi, servizi — e lo mette accanto al valore prodotto e al fatturato. Vedi lo scostamento quando succede, non a consuntivo.",
    perChi: 'Direzione · controllo di gestione · amministrazione · PM',
    problema: {
      title: 'Il margine si scopre quando è troppo tardi.',
      copy: "Il consuntivo arriva mesi dopo, ricostruito a mano da fatture, bolle e fogli presenze. Quando ti accorgi che una commessa perde, i soldi sono già spesi.",
    },
    capacita: [
      {
        title: 'Quadro economico',
        copy: 'Costo a oggi, valore prodotto, fatturato e margine operativo, separando il dato certo da quello provvisorio.',
      },
      {
        title: 'Manodopera al costo vero',
        copy: 'Le ore del rapportino valorizzate con il costo orario da CCNL di ogni persona, viaggi compresi.',
      },
      {
        title: 'Subappalti e SAL',
        copy: 'Contratti, stati di avanzamento e storni dei subappaltatori, imputati alla commessa giusta.',
      },
      {
        title: 'Materiali, mezzi e servizi',
        copy: 'DDT, ordini, magazzino, mezzi, carburanti, servizi e altri costi: ogni euro ha una commessa.',
      },
      {
        title: 'Avanzamento economico',
        copy: 'Quanto hai prodotto e quanto hai speso, commessa per commessa, aggiornato ogni giorno.',
      },
      {
        title: 'Costo esatto per lavorazione',
        copy: 'Il costo di ogni singola lavorazione calcolato in tempo reale, non solo per commessa.',
        inArrivo: true,
      },
    ],
    pull: 'Vedi lo scostamento mentre accade, intervieni in tempo, difendi il margine.',
  },
  {
    slug: 'presenze-buste-paga',
    code: '03',
    name: 'Presenze e buste paga',
    teaser: 'Edilizia e metalmeccanico: le ore del cantiere diventano il file per il consulente del lavoro, in un clic.',
    title: 'Le ore del cantiere,',
    mark: 'pronte per la busta paga.',
    lead: "Per le imprese con operai edili e metalmeccanici, la fine del mese è una caccia alle ore. Con BOSS le ore nascono in cantiere, gli straordinari si calcolano da soli e il file per il consulente del lavoro è pronto in un clic — nel formato che il tuo consulente già usa.",
    perChi: 'Ufficio paghe · ufficio personale · titolari · consulenti del lavoro',
    problema: {
      title: 'A fine mese, ogni busta paga è una telefonata.',
      copy: "Fogli presenze di carta, foto su WhatsApp, Excel da ricopiare. Straordinari contati a mano, cantieri sbagliati, persone dimenticate. Il consulente richiama, l’operaio contesta la busta, e il mese dopo si ricomincia.",
    },
    capacita: [
      {
        title: 'CCNL edilizia e metalmeccanico',
        copy: 'Le regole dei due contratti già dentro BOSS: ferie in ore o in giorni, festivi, viaggi, pioggia per gli edili.',
      },
      {
        title: 'Straordinari calcolati, non dichiarati',
        copy: 'Oltre le 8 ore al giorno o le 40 a settimana BOSS conta lo straordinario da solo, anche a cavallo del mese.',
      },
      {
        title: 'Il file per il consulente, in un clic',
        copy: 'Una griglia giornaliera persona × cantiere × voce di paga, già con i codici del tuo consulente. Lo scarichi, lo rivedi, lo invii.',
      },
      {
        title: 'Formati Inaz e TeamSystem',
        copy: 'Un file per ogni gestionale paghe, anche se le tue società usano consulenti diversi.',
      },
      {
        title: 'Prospetti allegati',
        copy: 'Cassa integrazione per maltempo, prospetto viaggi, un file per ogni agenzia interinale: tutto in un unico zip.',
      },
      {
        title: '12 controlli prima di scaricare',
        copy: 'Codici mancanti, cantieri non trovati, giornate incomplete o oltre le 12 ore: li vedi prima che li veda il consulente.',
      },
      {
        title: 'Invio diretto al consulente',
        copy: 'Il file parte da BOSS alla casella del consulente, con lo storico di cosa è stato inviato e quando.',
        inArrivo: true,
      },
      {
        title: 'Cassa Edile e trasferte',
        copy: 'Calcolo degli accantonamenti Cassa Edile e delle indennità di trasferta, direttamente dalle presenze.',
        inArrivo: true,
      },
      {
        title: 'Collegamento diretto ai gestionali paghe',
        copy: 'Integrazione con Inaz, TeamSystem e Zucchetti senza passare dal file.',
        inArrivo: true,
      },
    ],
    passi: [
      {
        title: 'Il caposquadra compila il rapportino',
        copy: 'Dal telefono: chi c’era, quante ore, su quale cantiere, chi guidava il furgone.',
      },
      {
        title: 'BOSS calcola e controlla',
        copy: 'Ordinario, straordinario, viaggi, festivi, ferie e permessi approvati. Le anomalie in rosso.',
      },
      {
        title: 'Un clic, e il consulente ha tutto',
        copy: 'Scarichi il file del mese con i prospetti allegati, lo rivedi e lo mandi al consulente.',
      },
    ],
    numeri: [
      { figure: '2', unit: 'CCNL principali', label: 'Edilizia e metalmeccanico, più gas-acqua, alimentari e dirigenti.' },
      { figure: '73', unit: 'voci di paga', label: 'Già mappate per ogni combinazione di contratto e inquadramento.' },
      { figure: '12', unit: 'controlli', label: 'Prima di scaricare il file, non dopo la telefonata del consulente.' },
      { figure: '1', unit: 'clic', label: 'Per avere il file del mese e tutti i prospetti allegati.' },
    ],
    pull: 'Le ore giuste, nella busta paga giusta.',
  },
  {
    slug: 'personale',
    code: '04',
    name: 'Personale e costo del lavoro',
    teaser: 'Anagrafica, contratti, scadenze e il costo orario di ogni persona calcolato dal CCNL.',
    title: 'Le persone, e quanto',
    mark: 'costano davvero.',
    lead: "Anagrafica del personale, storico dei contratti e scadenze in un solo posto. E per ogni persona il costo orario calcolato dal CCNL con il metodo delle tabelle ministeriali, spiegato passo per passo.",
    perChi: 'Ufficio personale · amministrazione · direzione',
    problema: {
      title: 'Il costo del lavoro è una stima.',
      copy: "Si usa un costo orario medio, uguale per tutti, aggiornato una volta l’anno. Così il costo della manodopera in commessa è sbagliato in partenza.",
    },
    capacita: [
      { title: 'Anagrafica del personale', copy: 'Dati, documenti, IBAN e storico del contratto di ogni dipendente.' },
      { title: 'Scadenze', copy: 'Visite, corsi, documenti e contratti in scadenza, prima che scadano.' },
      {
        title: 'Costo orario da CCNL',
        copy: 'Calcolato con il metodo delle tabelle ministeriali, con RAL, superminimi e ricarico delle agenzie.',
      },
      { title: 'Richieste e approvazioni', copy: 'Ferie, permessi, straordinari e smart working chiesti dall’app e approvati dal responsabile.' },
      { title: 'Import guidato', copy: 'Porti dentro il personale da un file in tre passi, senza ricominciare da zero.' },
      { title: 'Timesheet degli impiegati', copy: 'Gli impiegati segnano le ore sulle commesse, anche a mezz’ora.' },
    ],
    pull: 'Il costo del lavoro vero, persona per persona.',
  },
  {
    slug: 'acquisti-ddt',
    code: '05',
    name: 'Acquisti, DDT e magazzino',
    teaser: 'L’AI legge DDT e formulari, li abbina agli ordini e li porta in commessa.',
    title: 'I documenti dei fornitori,',
    mark: 'letti dall’AI.',
    lead: "BOSS legge i DDT e i formulari dei rifiuti, estrae i dati, li abbina agli ordini e li imputa alla commessa. L’ufficio controlla quello che l’AI segnala, invece di ricopiare tutto.",
    perChi: 'Ufficio acquisti · amministrazione · capocantiere · magazzino',
    problema: {
      title: 'Bolle nel cassetto, costi che arrivano tardi.',
      copy: "I DDT tornano dal cantiere a fine settimana, se tornano. Qualcuno li ricopia, li confronta con gli ordini, e intanto il costo non è in commessa.",
    },
    capacita: [
      {
        title: 'DDT letto dall’AI',
        copy: 'Data, numero, fornitore, partita IVA e righe estratti in automatico, con i campi incerti evidenziati.',
      },
      { title: 'Abbinamento agli ordini', copy: 'Le righe del DDT confrontate con l’ordine: differenze segnalate, documento contestabile.' },
      { title: 'Ordini e richieste di acquisto', copy: 'Dal cantiere parte la richiesta, l’ufficio fa l’ordine, tutto resta collegato.' },
      { title: 'Magazzino di cantiere', copy: 'Cosa è arrivato, cosa è stato usato, cosa resta, cantiere per cantiere.' },
      { title: 'Formulari rifiuti (FIR)', copy: 'Anche i formulari letti dall’AI e collegati alla commessa.' },
      { title: 'Mezzi e carburanti', copy: 'Parco mezzi, assegnazioni ai cantieri e carburanti nei costi di commessa.' },
    ],
    pull: 'Nessuna bolla nel cassetto. Ogni costo, subito in commessa.',
  },
  {
    slug: 'gare-offerte',
    code: '06',
    name: 'Gare e offerte',
    teaser: 'Dalla prima opportunità all’offerta, fino alla commessa che nasce già compilata.',
    title: 'Dalla gara alla commessa,',
    mark: 'senza ricopiare.',
    lead: "Opportunità e offerte su una bacheca per fase, con le email collegate. Quando la gara è vinta, la commessa nasce già compilata con i dati che avevi.",
    perChi: 'Ufficio gare · commerciale · direzione',
    problema: {
      title: 'Le gare stanno in un CRM, le commesse in un altro mondo.',
      copy: "Quando una gara è vinta, i dati si ricopiano a mano nel gestionale. E nessuno sa più quale versione dell’offerta era quella buona.",
    },
    capacita: [
      { title: 'Bacheca delle opportunità', copy: 'Ogni opportunità nella sua fase, dalla segnalazione all’aggiudicazione.' },
      { title: 'Offerte', copy: 'Le offerte collegate alla loro gara, con importi e stato.' },
      { title: 'Email collegate', copy: 'La corrispondenza di Outlook sulla scheda della gara, visibile a tutto il team.' },
      { title: 'Da gara vinta a commessa', copy: 'Un passaggio, e la commessa è creata con i dati della gara.' },
    ],
    pull: 'La gara vinta è già una commessa.',
  },
  {
    slug: 'direzione',
    code: '07',
    name: 'Cruscotto della direzione',
    teaser: 'Tutti i cantieri in una pagina: avanzamento, margine, compilazioni e chiusure.',
    title: 'Tutti i cantieri,',
    mark: 'in una pagina.',
    lead: "La direzione vede l’azienda per area e per commessa: cosa avanza, cosa perde, chi non ha compilato. Senza chiedere un report a nessuno.",
    perChi: 'Soci · direzione tecnica · direzione amministrativa',
    problema: {
      title: 'Per sapere come va, bisogna chiederlo.',
      copy: "Il quadro dell’azienda è la somma di dieci telefonate e cinque Excel, e arriva quando ormai è vecchio.",
    },
    capacita: [
      { title: 'Panoramica in tempo reale', copy: 'Le commesse attive, il loro avanzamento e il margine, aggiornati ogni giorno.' },
      { title: 'Vista per area', copy: 'Ogni area di business con i suoi numeri e i suoi responsabili.' },
      { title: 'Compilazioni e chiusure', copy: 'Chi ha compilato i rapportini, chi ha chiuso il mese, chi è indietro.' },
      {
        title: 'Chiedi alla commessa',
        copy: 'Un assistente AI a cui chiedere in italiano come va una commessa, un fornitore, un cantiere.',
        inArrivo: true,
      },
    ],
    pull: 'Il quadro completo, senza chiedere un report.',
  },
];

export function trovaFunzionalita(slug: string | undefined): Funzionalita | undefined {
  return FUNZIONALITA.find((f) => f.slug === slug);
}
